/* ============================================================
   PINTÓN — theme.js
   Cart drawer (AJAX) · Menu drawer · Predictive search · A11y
   Loaded with defer so DOM is ready when this runs.
   ============================================================ */
(function () {
  'use strict';

  /* -------------------- Helpers -------------------- */

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  // El símbolo y el formato se leen de la tienda; escribir "$" a mano y dividir
  // entre 100 rompe en cuanto cambia la moneda o el idioma.
  const formatMoney = (cents) => {
    const n = (Number(cents) || 0) / 100;
    try {
      const moneda = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      /* A20: en dólares se escribe siempre «$319.64», como el filtro `money` de
         Liquid en los dos idiomas. Con el idioma de la página, en /es salía
         «319,64 US$» al lado de líneas en «$216.00». */
      const idioma = moneda === 'USD' ? 'en-US'
        : ((window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en');
      return new Intl.NumberFormat(idioma, { style: 'currency', currency: moneda }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  };

  const trapFocus = (container) => {
    const focusables = container.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return () => {};
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  };

  /* -------------------- Drawer controller -------------------- */

  class Drawer {
    constructor(el) {
      this.el = el;
      this.id = el.id;
      this.releaseFocus = null;
      this.lastFocused = null;

      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.matches('.drawer__overlay')) {
          this.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && el.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });
    }

    open() {
      this.lastFocused = document.activeElement;
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      this.releaseFocus = trapFocus(this.el);
      const firstFocus = this.el.querySelector('[data-drawer-initial-focus]')
        || this.el.querySelector('button, input, a');
      if (firstFocus) firstFocus.focus();
      this.el.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close() {
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
      if (this.releaseFocus) this.releaseFocus();
      if (this.lastFocused) this.lastFocused.focus();
      this.el.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }

  const drawers = {};
  $$('[data-drawer]').forEach((el) => {
    drawers[el.id] = new Drawer(el);
  });

  $$('[data-drawer-open]').forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const id = trigger.getAttribute('data-drawer-open');
      if (drawers[id]) drawers[id].open();
    });
  });

  // Load cart upsell when cart drawer opens
  const cartDrawerEl = $('#cart-drawer');
  if (cartDrawerEl) {
    cartDrawerEl.addEventListener('drawer:open', () => loadCartUpsell());
  }

  /* -------------------- Cart (AJAX) -------------------- */

  const Cart = {
    async fetch() {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    },

    async add(formOrPayload) {
      const isForm = formOrPayload instanceof HTMLFormElement;
      const body = isForm ? new FormData(formOrPayload) : JSON.stringify(formOrPayload);
      const headers = isForm
        ? { Accept: 'application/json' }
        : { 'Content-Type': 'application/json', Accept: 'application/json' };
      const res = await fetch('/cart/add.js', { method: 'POST', headers, body, lindoPropio: true });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw err;
      }
      return res.json();
    },

    async change(payload) {
      const res = await fetch('/cart/change.js', {
        lindoPropio: true,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      return res.json();
    },

    async update(updates) {
      const res = await fetch('/cart/update.js', {
        lindoPropio: true,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      return res.json();
    },

    async clear() {
      await fetch('/cart/clear.js', { method: 'POST' });
      return this.fetch();
    },
  };

  const updateCartCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.setAttribute('data-empty', count === 0 ? 'true' : 'false');
    });
  };

  // El carrito tiene DOS cifras distintas y no son intercambiables:
  //   items_subtotal_price  antes de descuentos
  //   total_price           lo que se cobra de verdad
  // El botón de pagar debe anunciar SIEMPRE total_price.
  const updateCartSubtotal = (cents, total) => {
    $$('[data-cart-subtotal]').forEach((el) => {
      el.textContent = formatMoney(cents);
    });
    if (typeof total === 'number') {
      $$('[data-cart-total]').forEach((el) => {
        el.textContent = formatMoney(total);
      });
    }
  };

  const mostrarErrorCarrito = (mensaje) => {
    const huecos = $$('[data-cart-errors]');
    if (!huecos.length) {
      // Sin hueco donde escribirlo, el error sería invisible. Mejor un aviso
      // del navegador que un producto agotado que no dice nada.
      if (mensaje) window.alert(mensaje);
      return;
    }
    huecos.forEach((el) => {
      el.textContent = mensaje || '';
      el.hidden = !mensaje;
    });
  };

  const updateFreeShippingBar = (cents) => {
    const bar = $('[data-free-shipping-bar]');
    if (!bar) return;
    const threshold = parseFloat(bar.getAttribute('data-threshold') || '0') * 100;
    if (threshold <= 0) return;
    const remaining = Math.max(0, threshold - cents);
    const progress = Math.min(100, (cents / threshold) * 100);
    bar.style.setProperty('--progress', `${progress}%`);
    const remEl = $('[data-free-shipping-remaining]', bar);
    const wonEl = $('[data-free-shipping-won]', bar);
    if (remaining > 0) {
      if (remEl) {
        remEl.hidden = false;
        const amount = remEl.querySelector('[data-amount]');
        if (amount) amount.textContent = formatMoney(remaining);
      }
      if (wonEl) wonEl.hidden = true;
    } else {
      if (remEl) remEl.hidden = true;
      if (wonEl) wonEl.hidden = false;
    }
  };

  /* ------------------------------------------------------------------------
     QUE EL CAJON SOBREVIVA A LA RECARGA DE UNA APP

     `Shopify.actions.openCart`, sin configurar, «falls back to a full page
     reload when it doesn't recognize how your theme renders its cart». Este
     tema no se parece a Dawn, asi que ninguna app lo reconoce: la de descuentos
     por volumen recarga la pagina al añadir y se lleva el cajon por delante.
     El comprador pulsa «Add to cart», el producto entra... y no ve nada.
     https://shopify.dev/docs/api/storefront-events-and-actions/actions/open-cart

     Dos frentes, y aqui estan los dos: abajo se configura el contrato oficial
     para que las apps que lo usen dejen de recargar, y aqui se aguanta la
     recarga de quien no lo use. Si la pagina se va justo despues de añadir, se
     deja una marca fechada y al volver se reabre el cajon.

     La marca es de vida corta a proposito: solo vale si la pagina se fue dentro
     de la ventana siguiente a un «añadir». Portado de shopjowi.com, donde se
     resolvio el mismo caso con la misma app. */
  const RESTORE_KEY = 'lindo:cart-open';
  const RESTORE_ARM_MS = 5000;    // margen para que la app recargue
  const RESTORE_MAX_MS = 15000;   // caducidad al leer, por si quedo huerfana
  let restoreTimer = null;

  const restoreClear = () => {
    if (restoreTimer) { clearTimeout(restoreTimer); restoreTimer = null; }
    try { sessionStorage.removeItem(RESTORE_KEY); } catch (e) { /* modo privado */ }
  };

  const restoreArm = () => {
    try { sessionStorage.setItem(RESTORE_KEY, String(Date.now())); } catch (e) { return; }
    if (restoreTimer) clearTimeout(restoreTimer);
    restoreTimer = setTimeout(restoreClear, RESTORE_ARM_MS);
  };

  const restorePending = () => {
    let marca = null;
    try { marca = sessionStorage.getItem(RESTORE_KEY); } catch (e) { return false; }
    if (!marca) return false;
    restoreClear();
    const t = parseInt(marca, 10);
    return !Number.isNaN(t) && Date.now() - t < RESTORE_MAX_MS;
  };

  /* Si el comprador se va por su propio pie -un enlace, «ver carrito», pagar-,
     la marca se cae: reabrirle el cajon encima de la pagina que ha pedido seria
     peor que no hacer nada. Solo sobrevive la navegacion que NO ha pedido el. */
  document.addEventListener('click', (e) => {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('a[href]') || e.target.closest('[name="checkout"]')) restoreClear();
  }, true);

  document.addEventListener('drawer:close', (e) => {
    if (e.target && e.target.id === 'cart-drawer') restoreClear();
  });

  const refreshCartUI = async () => {
    const cart = await Cart.fetch();
    updateCartCount(cart.item_count);
    updateCartSubtotal(cart.items_subtotal_price || cart.total_price, cart.total_price);
    updateFreeShippingBar(cart.items_subtotal_price || cart.total_price);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  };

  // Re-render the cart drawer body using the cart section_id if it exists
  const refreshCartDrawer = async () => {
    const drawer = $('#cart-drawer');
    if (!drawer) return refreshCartUI();
    const sectionId = drawer.getAttribute('data-section-id');
    if (!sectionId) return refreshCartUI();

    try {
      const res = await fetch(`${window.location.pathname}?sections=${sectionId}`);
      const data = await res.json();
      const html = data[sectionId];
      if (html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newInner = parsed.querySelector(`[data-section-id="${sectionId}"]`);
        if (newInner) drawer.innerHTML = newInner.innerHTML;
      }
    } catch (e) {
      /* fall back to raw counts */
    }
    await refreshCartUI();
    loadCartUpsell();
    return;
  };

  /* A20: en /cart los +/− y la X solo repintaban el cajón. La tabla se quedaba
     con la cantidad y el precio viejos, y el segundo «+» partía de ese número
     viejo (medido 01/10: 1 → 2 → 2). Ahora se repinta también la sección del
     resumen de /cart con la Section Rendering API. */
  const refreshCartPage = async () => {
    const resumen = document.querySelector('.cart-summary-section');
    const envoltorio = resumen && resumen.closest('[id^="shopify-section-"]');
    if (!envoltorio) return;
    const sectionId = envoltorio.id.replace('shopify-section-', '');
    try {
      const res = await fetch(`${window.location.pathname}?sections=${sectionId}`);
      const data = await res.json();
      if (!data[sectionId]) return;
      const parsed = new DOMParser().parseFromString(data[sectionId], 'text/html');
      const nuevo = parsed.getElementById(envoltorio.id);
      envoltorio.innerHTML = nuevo ? nuevo.innerHTML : parsed.body.innerHTML;
    } catch (e) {
      window.location.reload();
    }
  };

  /* N1 · 02/10/2026 · La app de ofertas (AOV) aplica el descuento por cantidad
     con su PROPIO /cart/update.js justo DESPUÉS de que el tema repinta. Medido en
     la auditoría del 01/10: tras volver del checkout, el primer «+» dejaba la tabla
     con el precio sin descuento hasta el siguiente clic. Ahora se escuchan las
     llamadas de carrito que NO hace el tema (fetch y XHR) y, cuando terminan, se
     repinta UNA vez, y solo si /cart.js cambió de verdad (la firma corta el bucle
     con apps que reaccionan a `cart:updated`). Las llamadas del tema llevan
     `lindoPropio: true`, que fetch ignora. */
  (() => {
    const esCarrito = (u) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(u || ''));
    const firma = (c) => JSON.stringify([
      c.total_price,
      (c.items || []).map((i) => [i.key, i.quantity, i.final_line_price]),
      (c.cart_level_discount_applications || []).map((d) => d.title),
    ]);
    let ultima = null;
    let espera = null;
    const repintar = () => {
      clearTimeout(espera);
      espera = setTimeout(async () => {
        try {
          const c = await Cart.fetch();
          const f = firma(c);
          if (f === ultima) return;
          ultima = f;
          await refreshCartDrawer();
          await refreshCartPage();
        } catch (e) { /* sin red: el siguiente clic lo corrige */ }
      }, 300);
    };
    try {
      const fetchOriginal = window.fetch;
      window.fetch = function (recurso, init) {
        const p = fetchOriginal.apply(this, arguments);
        try {
          const url = typeof recurso === 'string' ? recurso : (recurso && recurso.url) || '';
          if (esCarrito(url) && !(init && init.lindoPropio)) p.then(repintar, () => {});
        } catch (e) { /* nada */ }
        return p;
      };
      const abrir = XMLHttpRequest.prototype.open;
      const enviar = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (metodo, url) {
        this.__lindoUrl = url;
        return abrir.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        if (esCarrito(this.__lindoUrl)) this.addEventListener('loadend', repintar);
        return enviar.apply(this, arguments);
      };
    } catch (e) {
      console.warn('[cart] no se ha podido escuchar las llamadas de las apps', e);
    }
  })();

  // Cart upsell — Product Recommendations API with complementary→related fallback
  const cartUpsellCache = new Map();
  let cartUpsellAbort = null;

  async function loadCartUpsell() {
    const drawer = $('#cart-drawer');
    if (!drawer) return;
    const slot = drawer.querySelector('[data-cart-upsell]');
    if (!slot) return;

    const lastItem = drawer.querySelector('[data-cart-item]:last-of-type');
    const productId = lastItem ? lastItem.getAttribute('data-product-id') : null;
    if (!productId) {
      slot.innerHTML = '';
      return;
    }

    /* A19: las sugerencias dependen de TODO el carrito (no se ofrece lo que
       ya esta dentro), asi que la cache va por producto + contenido. */
    const cacheKey = productId + '|' + Array.from(drawer.querySelectorAll('[data-cart-item]'))
      .map((li) => li.getAttribute('data-product-id')).join(',');
    if (cartUpsellCache.has(cacheKey)) {
      slot.innerHTML = cartUpsellCache.get(cacheKey);
      return;
    }

    if (cartUpsellAbort) cartUpsellAbort.abort();
    cartUpsellAbort = new AbortController();

    const fetchRecs = async (intent) => {
      const url = `${window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/'}recommendations/products?product_id=${productId}&intent=${intent}&limit=4&section_id=cart-recommendations`;
      const res = await fetch(url, { signal: cartUpsellAbort.signal });
      return res.ok ? res.text() : '';
    };

    const extractContent = (html) => {
      if (!html) return '';
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-cart-upsell-content]');
      return content ? content.outerHTML : '';
    };

    try {
      let html = await fetchRecs('complementary');
      let content = extractContent(html);
      if (!content) {
        html = await fetchRecs('related');
        content = extractContent(html);
      }
      cartUpsellCache.set(cacheKey, content);
      slot.innerHTML = content;
    } catch (err) {
      if (err.name === 'AbortError') return;
      slot.innerHTML = '';
    }
  }

  // Intercept product forms for AJAX add-to-cart
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action$="/cart/add"]') && !form.matches('form[data-ajax-cart]')) return;
    e.preventDefault();

    const submitBtn = form.querySelector('[type="submit"]');
    // Se guarda el innerHTML, no el textContent: el botón lleva <span> dentro
    // (el precio, entre otros) y devolverle solo el texto los destruía en el
    // primer clic.
    const originalLabel = submitBtn ? submitBtn.innerHTML : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }

    try {
      await Cart.add(form);
      /* La marca se arma AQUI, no despues de repintar. La app de descuentos
         recarga la pagina en cuanto ve el /cart/add, y a veces llega mientras
         `refreshCartDrawer()` todavia esta pidiendo la seccion: entonces no se
         ejecutaba nada de lo de abajo, la marca no se ponia y el cajon no se
         reabria al volver. Medido en la tienda el 10/09/2026. */
      restoreArm();
      await refreshCartDrawer();
      if (drawers['cart-drawer']) drawers['cart-drawer'].open();
      form.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true }));
    } catch (err) {
      const msg = (err && err.description) || (err && err.message) || 'No se pudo añadir al carrito';
      // El hueco [data-cart-errors] no existía en la ficha, así que los errores
      // eran invisibles: producto agotado sin un solo mensaje.
      const errEl = form.querySelector('[data-cart-errors]');
      if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
      else mostrarErrorCarrito(msg);
      console.error('[cart]', err);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
        submitBtn.innerHTML = originalLabel;
      }
    }
  });

  // Quantity steppers + remove buttons inside cart
  /* 02/10/2026 · Auditoría móvil/escritorio: con menos de ~1,6 s entre clics, el
     2.º «+» se perdía, porque leía la casilla antes de que volviera el repintado y
     mandaba la misma cantidad. Ahora la casilla se pone al momento (el siguiente
     clic parte de ahí), los cambios van EN COLA y en orden, y se repinta una sola
     vez cuando la cola se vacía. */
  let carritoPendientes = 0;
  let carritoCola = Promise.resolve();
  const encolarCarrito = (tarea) => {
    carritoPendientes += 1;
    carritoCola = carritoCola
      .then(tarea)
      .catch((err) => console.error('[cart]', err))
      .then(async () => {
        carritoPendientes -= 1;
        if (carritoPendientes === 0) {
          await refreshCartDrawer();
          await refreshCartPage();
        }
      });
    return carritoCola;
  };

  document.addEventListener('click', (e) => {
    const stepper = e.target.closest('[data-cart-stepper]');
    const remove = e.target.closest('[data-cart-remove]');

    if (stepper) {
      e.preventDefault();
      const line = parseInt(stepper.getAttribute('data-line'), 10);
      const delta = parseInt(stepper.getAttribute('data-delta'), 10) || 0;
      const inputs = $$(`[data-cart-qty-input][data-line="${line}"]`);
      const actual = parseInt(inputs[0] ? inputs[0].value : '1', 10) || 1;
      const newQty = Math.max(0, actual + delta);
      inputs.forEach((i) => { i.value = newQty; });
      encolarCarrito(() => Cart.change({ line, quantity: newQty }));
    }

    if (remove) {
      e.preventDefault();
      const line = parseInt(remove.getAttribute('data-line'), 10);
      encolarCarrito(() => Cart.change({ line, quantity: 0 }));
    }
  });

  /* -------------------- Product card (Pipeline-style) -------------------- */
  // Image scrubber + swatch click + size pill click. Event delegation from document
  // so it survives AJAX re-renders of the collection grid.

  function getCardVariants(card) {
    const script = card.querySelector('[data-pcard-variants]');
    if (!script) return [];
    try {
      const cached = card._variantsCache;
      if (cached) return cached;
      const data = JSON.parse(script.textContent);
      card._variantsCache = data;
      return data;
    } catch (e) {
      return [];
    }
  }

  function getActiveColor(card) {
    return card.querySelector('[data-pcard-color-name]')?.textContent.trim() || null;
  }

  function setActiveColor(card, colorName) {
    const el = card.querySelector('[data-pcard-color-name]');
    if (el) el.textContent = colorName;
  }

  // Abbreviate labels to shortest unique prefix (used for color pills when names are long)
  function abbreviateLabels(labels) {
    const result = {};
    for (const label of labels) {
      let len = 1;
      let prefix = label.slice(0, len).toUpperCase();
      while (
        len < label.length &&
        labels.some(other => other !== label && other.toUpperCase().startsWith(prefix))
      ) {
        len++;
        prefix = label.slice(0, len).toUpperCase();
      }
      result[label] = prefix;
    }
    return result;
  }

  function renderSizesForCard(card) {
    const sizesEl = card.querySelector('[data-pcard-sizes]');
    if (!sizesEl) return;
    const variants = getCardVariants(card);
    if (!variants.length) return;

    const colorPos = parseInt(card.getAttribute('data-color-position'), 10);
    const sizePos = parseInt(card.getAttribute('data-size-position'), 10);
    const activeColor = getActiveColor(card);
    const isColorMode = !sizePos && !!colorPos;

    let entries;
    if (sizePos) {
      // Build size pills filtered by active color
      const sizeIdx = sizePos - 1;
      const colorIdx = colorPos ? colorPos - 1 : -1;
      const seen = new Set();
      entries = variants
        .filter(v => colorIdx < 0 || v.opts[colorIdx] === activeColor)
        .filter(v => { if (seen.has(v.opts[sizeIdx])) return false; seen.add(v.opts[sizeIdx]); return true; })
        .map(v => ({ id: v.id, label: v.opts[sizeIdx], available: v.avail }));
    } else if (colorPos) {
      // Single-option color: pills are colors
      const colorIdx = colorPos - 1;
      const seen = new Set();
      entries = variants
        .filter(v => { if (seen.has(v.opts[colorIdx])) return false; seen.add(v.opts[colorIdx]); return true; })
        .map(v => ({ id: v.id, label: v.opts[colorIdx], available: v.avail }));
    } else {
      entries = [];
    }

    // Apply abbreviation for color mode (long names like PURPLE/BLACK don't fit)
    const labels = entries.map(e => e.label);
    const abbrs = isColorMode ? abbreviateLabels(labels) : null;

    sizesEl.innerHTML = entries.map(e => {
      const display = abbrs ? abbrs[e.label] : e.label;
      const soldOutLabel = (window.themeLocales && window.themeLocales.sold_out) || 'Sold out';
      const ariaLabel = e.available ? 'Add ' + e.label + ' to Cart' : e.label + ' — ' + soldOutLabel;
      return `<li role="none"><button type="button"
        class="product-card__size-pill"
        data-pcard-size-pill
        data-variant-id="${e.id}"
        data-full-label="${e.label.replace(/"/g, '&quot;')}"
        role="menuitem"
        title="${e.label.replace(/"/g, '&quot;')}"
        ${e.available ? '' : 'disabled'}
        aria-label="${ariaLabel.replace(/"/g, '&quot;')}">
        ${display}
      </button></li>`;
    }).join('');
  }

  function updateCardImageForColor(card, colorName) {
    const variants = getCardVariants(card);
    if (!variants.length) return;
    const colorPos = parseInt(card.getAttribute('data-color-position'), 10);
    if (!colorPos) return;
    const colorIdx = colorPos - 1;
    const target = variants.find(v => v.opts[colorIdx] === colorName && v.img);
    if (!target || !target.img) return;

    const firstImg = card.querySelector('[data-pcard-img][data-image-idx="0"]');
    if (firstImg && firstImg.getAttribute('src') !== target.img) {
      firstImg.src = target.img;
      firstImg.srcset = '';
    }
  }

  // Image auto-cycle on hover (no mousemove needed)
  const cycleInterval = 900; // ms per image
  const activeCycles = new WeakMap();

  function setCardActiveImage(card, idx) {
    card.querySelectorAll('[data-pcard-img]').forEach((img) => {
      img.classList.toggle('is-active', parseInt(img.getAttribute('data-image-idx'), 10) === idx);
    });
    card.querySelectorAll('[data-bar]').forEach((bar) => {
      bar.classList.toggle('is-active', parseInt(bar.getAttribute('data-bar'), 10) === idx);
    });
  }

  function startCardCycle(card) {
    if (activeCycles.has(card)) return;
    const imagesCount = parseInt(card.getAttribute('data-images-count'), 10) || 1;
    if (imagesCount < 2) return;
    // Eager-load extra images on hover-intent
    if (!card._imagesEager) {
      card._imagesEager = true;
      card.querySelectorAll('[data-pcard-img]').forEach((img) => {
        if (img.loading === 'lazy') img.loading = 'eager';
      });
    }
    let idx = 0;
    const interval = setInterval(() => {
      if (document.hidden) return;
      idx = (idx + 1) % imagesCount;
      setCardActiveImage(card, idx);
    }, cycleInterval);
    activeCycles.set(card, interval);
  }

  function stopCardCycle(card) {
    const interval = activeCycles.get(card);
    if (interval) {
      clearInterval(interval);
      activeCycles.delete(card);
    }
    setCardActiveImage(card, 0);
  }

  document.addEventListener('mouseenter', (e) => {
    const card = e.target.closest && e.target.closest('[data-product-card]');
    if (!card) return;
    startCardCycle(card);
  }, true);

  document.addEventListener('mouseleave', (e) => {
    const card = e.target.closest && e.target.closest('[data-product-card]');
    if (!card) return;
    stopCardCycle(card);
  }, true);

  // Swatch click → swap active variant image + color name + re-render sizes
  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('[data-pcard-swatch]');
    if (!swatch) return;
    e.preventDefault();
    const card = swatch.closest('[data-product-card]');
    if (!card) return;

    const colorName = swatch.getAttribute('data-color');
    const variantImg = swatch.getAttribute('data-variant-image');

    card.querySelectorAll('[data-pcard-swatch]').forEach((s) => {
      const isThis = s === swatch;
      s.classList.toggle('is-active', isThis);
      s.setAttribute('aria-pressed', isThis ? 'true' : 'false');
    });

    setActiveColor(card, colorName);

    if (variantImg) {
      const firstImg = card.querySelector('[data-pcard-img][data-image-idx="0"]');
      if (firstImg) {
        firstImg.src = variantImg;
        firstImg.srcset = '';
      }
    } else {
      updateCardImageForColor(card, colorName);
    }

    // Update PDP link href if active variant has matching variant ID
    const variants = getCardVariants(card);
    if (variants.length) {
      const colorPos = parseInt(card.getAttribute('data-color-position'), 10);
      if (colorPos) {
        const first = variants.find(v => v.opts[colorPos - 1] === colorName && v.avail) || variants.find(v => v.opts[colorPos - 1] === colorName);
        if (first) {
          const productUrl = card.getAttribute('data-product-url');
          card.querySelectorAll('a[href]').forEach((a) => {
            if (a.href.includes(productUrl)) {
              a.href = `${productUrl}?variant=${first.id}`;
            }
          });
        }
      }
    }
  });

  // Hover/focus on quick-add: build size pills if not yet built
  document.addEventListener('mouseenter', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-pcard-quick-add-trigger]');
    if (!trigger) return;
    const card = trigger.closest('[data-product-card]');
    if (!card) return;
    const sizesEl = card.querySelector('[data-pcard-sizes]');
    if (sizesEl && !sizesEl.children.length) {
      renderSizesForCard(card);
    }
  }, true);

  document.addEventListener('focusin', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-pcard-quick-add-trigger]');
    if (!trigger) return;
    const card = trigger.closest('[data-product-card]');
    if (!card) return;
    const sizesEl = card.querySelector('[data-pcard-sizes]');
    if (sizesEl && !sizesEl.children.length) {
      renderSizesForCard(card);
    }
    const qa = card.querySelector('[data-pcard-quick-add]');
    if (qa) qa.setAttribute('data-state', 'sizes-open');
  });

  // Cart notification banner (toast-style, no drawer auto-open)
  let cartNotifTimeout = null;
  let lastAddedPill = null;
  let lastAddedPillReset = null;

  function ensureCartNotification() {
    let banner = document.querySelector('[data-cart-notification]');
    if (banner) return banner;
    banner = document.createElement('div');
    banner.setAttribute('data-cart-notification', '');
    banner.className = 'cart-notification';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    const addedLabel = window.themeLocales && window.themeLocales.cart_added || 'ADDED TO CART';
    const viewLabel = window.themeLocales && window.themeLocales.view_cart || 'VIEW CART';
    banner.dataset.addedLabel = addedLabel;
    banner.innerHTML = `
      <div class="cart-notification__inner">
        <span class="cart-notification__text" data-cart-notification-text></span>
        <button type="button" class="cart-notification__view" data-cart-notification-view>${viewLabel}</button>
      </div>
    `;
    document.body.appendChild(banner);

    banner.querySelector('[data-cart-notification-view]').addEventListener('click', () => {
      if (drawers['cart-drawer']) drawers['cart-drawer'].open();
      banner.classList.remove('is-active');
    });
    return banner;
  }

  function showCartNotification(productTitle, sizeLabel, sourcePill) {
    const banner = ensureCartNotification();
    const text = banner.querySelector('[data-cart-notification-text]');
    const sizePart = sizeLabel ? ` ${sizeLabel}` : '';
    text.textContent = `${productTitle}${sizePart}  ${banner.dataset.addedLabel}`;

    // Reset previously marked pill
    if (lastAddedPillReset) {
      lastAddedPillReset();
      lastAddedPillReset = null;
    }

    if (sourcePill) {
      const originalHTML = sourcePill.innerHTML;
      sourcePill.classList.add('is-added');
      sourcePill.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
      sourcePill.disabled = true;
      lastAddedPill = sourcePill;
      lastAddedPillReset = () => {
        sourcePill.classList.remove('is-added');
        sourcePill.innerHTML = originalHTML;
        sourcePill.disabled = false;
      };
    }

    banner.classList.add('is-active');
    clearTimeout(cartNotifTimeout);
    cartNotifTimeout = setTimeout(() => {
      banner.classList.remove('is-active');
      if (lastAddedPillReset) {
        lastAddedPillReset();
        lastAddedPillReset = null;
      }
    }, 4000);
  }

  // Size pill click → AJAX add to cart + show banner notification
  document.addEventListener('click', async (e) => {
    const pill = e.target.closest('[data-pcard-size-pill]');
    if (!pill) return;
    if (pill.disabled) return;
    e.preventDefault();
    const variantId = pill.getAttribute('data-variant-id');
    if (!variantId) return;
    pill.setAttribute('aria-busy', 'true');

    const card = pill.closest('[data-product-card]');
    const productTitle = card ? (card.querySelector('.product-card__title a')?.textContent.trim() || '') : '';
    const sizeLabel = pill.getAttribute('data-full-label') || pill.textContent.trim();

    try {
      await Cart.add({ id: variantId, quantity: 1 });
      await refreshCartDrawer();
      showCartNotification(productTitle, sizeLabel, pill);
    } catch (err) {
      console.error('[card quick-add]', err);
    } finally {
      pill.removeAttribute('aria-busy');
    }
  });

  // Listen also for cart:item-added events (e.g. from main product form) → show banner
  document.addEventListener('cart:item-added', (e) => {
    const form = e.target;
    if (!form || !form.matches) return;
    // Skip the cart drawer's own quantity changes etc.
    if (form.closest('[data-cart-items]')) return;
    if (form.matches('[data-product-card] form')) return; // already handled above (it's a direct submit form on card)
  });

  /* -------------------- Predictive search -------------------- */

  const searchModal = $('#search-modal');
  if (searchModal) {
    const input = $('[data-search-input]', searchModal);
    const resultsEl = $('[data-search-results]', searchModal);
    let abortController;
    let debounceTimer;

    const runSearch = async (term) => {
      if (!term || term.length < 2) {
        if (resultsEl) resultsEl.innerHTML = '';
        return;
      }
      if (abortController) abortController.abort();
      abortController = new AbortController();
      try {
        const url = `/search/suggest?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: abortController.signal });
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        if (section && resultsEl) resultsEl.innerHTML = section.innerHTML;
      } catch (err) {
        if (err.name !== 'AbortError') console.error('[search]', err);
      }
    };

    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => runSearch(input.value.trim()), 200);
      });
    }
  }

  /* -------------------- Sticky header on scroll -------------------- */

  const header = $('[data-header-sticky]');
  if (header) {
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      header.classList.toggle('is-hidden', y > lastY && y > 120);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Announcement bar rotation: moved to {% javascript %} of the section
     for better scope isolation. See sections/announcement-bar.liquid */

  /* -------------------- Discount code copy -------------------- */

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy-code]');
    if (!btn) return;
    const code = btn.getAttribute('data-copy-code');
    if (!code || !navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => {
      const original = btn.textContent;
      btn.textContent = (window.themeLocales && window.themeLocales.copied) || 'Copied!';
      btn.setAttribute('aria-live', 'polite');
      setTimeout(() => { btn.textContent = original; }, 1800);
    });
  });

  /* -------------------- Init -------------------- */

  refreshCartUI();

  /* Reapertura tras una recarga ajena (ver el bloque RESTORE_KEY). En /cart no:
     alli el carrito ya es la pagina y taparla con el cajon sobra. */
  if (restorePending()
      && drawers['cart-drawer']
      && document.body.className.indexOf('template-cart') === -1) {
    drawers['cart-drawer'].open();
  }

  /* ------------------------------------------------------------------------
     CONTRATO OFICIAL CON LAS APPS — Shopify.actions
     https://shopify.dev/docs/api/storefront-events-and-actions/actions/configure

     Se le dice al escaparate como se abre este cajon y como se repinta, para
     que `openCart()` y `updateCart()` de cualquier app hagan lo correcto en
     lugar de recargar la pagina entera.

     Aviso de la documentacion: «Only the first configure call on an action
     takes effect». theme.js va con `defer`, asi que no se puede garantizar ser
     el primero; se comprueba el valor devuelto y se deja constancia. */
  try {
    const acciones = window.Shopify && window.Shopify.actions;

    if (acciones && acciones.openCart && typeof acciones.openCart.configure === 'function') {
      const ok = acciones.openCart.configure({
        handler() {
          if (drawers['cart-drawer']) { drawers['cart-drawer'].open(); return; }
          window.location.href = '/cart';
        },
      });
      if (ok === false) console.warn('[cart] openCart ya lo habia configurado otro script');
    }

    if (acciones && acciones.updateCart && typeof acciones.updateCart.configure === 'function') {
      const ok = acciones.updateCart.configure({
        eventTarget: () => document.getElementById('cart-drawer') || document,
        async handler(defaultHandler) {
          const resultado = await defaultHandler();
          await refreshCartUI();
          return resultado;
        },
      });
      if (ok === false) console.warn('[cart] updateCart ya lo habia configurado otro script');
    }
  } catch (err) {
    console.warn('[cart] no se ha podido configurar Shopify.actions', err);
  }

  // Expose for sections / debugging
  window.ShopTheme = { Cart, drawers, refreshCartDrawer, refreshCartUI };
})();
